/**
 * Fonte única dos valores enumerados do domínio.
 *
 * Cada constante é consumida em dois lugares: pelo Drizzle em `text({ enum })`
 * e pelo Zod em `z.enum()`. No sistema anterior os dois lados eram declarados
 * separadamente, o que permitia divergirem em silêncio.
 */
/**
 * `cliente` é o dono de uma conta do SaaS: opera como um operador, mas apenas
 * sobre os próprios dados, e ganha as telas de conta (pacote, custos, kiosk).
 * `operador` e `kiosk` pertencem a um cliente via `users.owner_user_id`.
 */
export const USER_ROLES = ["admin", "cliente", "operador", "kiosk"];
/**
 * De qual WhatsApp saem as fotos de um cliente.
 *
 * `nenhum` é o estado de uma conta nova: enviar pelo número de outra casa sem
 * ninguém ter decidido isso seria pior do que não enviar. `sistema` depende de
 * liberação do admin; `proprio` depende de o cliente conectar o número dele.
 */
export const WHATSAPP_MODOS = ["nenhum", "sistema", "proprio"];
/**
 * Onde está o envio de uma foto pelo WhatsApp (#105).
 *
 * `na_fila` cobre o tempo de espera e as novas tentativas: para quem olha a tela, é tudo "ainda vai". `nao_enviada`
 * só quando não há mais o que tentar — número sem WhatsApp, configuração errada, ou as tentativas acabaram.
 */
export const ESTADOS_DO_ENVIO_WHATSAPP = ["na_fila", "enviada", "nao_enviada"];
/** Segmentos e interesses aceitos pelo formulário comercial público. */
export const LANDING_TIPOS_EVENTO = [
    "congresso",
    "encontro",
    "livraria",
    "editora",
    "estande",
    "lancamento",
    "outro",
];
export const LANDING_INTERESSES = [
    "presente",
    "livro",
    "estande",
    "lancamento",
    "telao",
    "outro",
];
/** Allowlist: um navegador não pode transformar analytics em armazenamento livre. */
export const LANDING_EVENTOS = [
    "hero_cta_click",
    "secondary_cta_click",
    "use_case_view",
    "use_case_select",
    "gallery_interaction",
    "personalization_compare",
    "kiosk_demo_play",
    "feature_tab_open",
    "faq_open",
    "form_start",
    "form_step_complete",
    "form_submit",
    "form_error",
    "whatsapp_click",
    "login_click",
];
export const GENERATION_STATUSES = ["pending", "processing", "completed", "failed"];
/**
 * De onde veio a imagem publicada de uma geração: da IA, ou da própria fotografia, sem IA — o atendimento "Só
 * fotografia", em que a foto sai como foi tirada, para imprimir (#95).
 */
export const ORIGENS_DA_GERACAO = ["ia", "fotografia"];
export const BACKUP_STATUSES = [
    "not_started",
    "queued",
    "processing",
    "completed",
    "failed",
];
export const INSTITUTION_STATUSES = ["active", "inactive"];
export const VIVENCIA_STATUSES = ["active", "inactive"];
/**
 * Valores conforme gravados no sistema anterior — são rótulos em português que
 * já existem no banco de produção. Mudá-los quebraria a migração de dados.
 */
export const PURCHASE_TYPES = [
    "Camisa",
    "Livro",
    "Ingresso de Evento",
    "Assinatura ETV",
    "Participante Grupo de Estudo",
];
export const DIVULGACAO_MEDIA_TYPES = ["image", "video"];
export const DIVULGACAO_STATUSES = ["active", "inactive"];
export const PROMPT_TYPES = ["image"];
/**
 * `general`, `image` e `storage` já existem no banco anterior e migram direto.
 * As linhas de categoria `tts` e `stt` simplesmente não são migradas.
 */
export const API_CONFIG_CATEGORIES = [
    "general",
    "image",
    "storage",
    "whatsapp",
    "cost",
    "galeria",
    /** Entrada de conta: como a pessoa cria conta e entra. */
    "auth",
    /** E-mail transacional: recibo de compra, aviso de pacote. */
    "email",
    /** Cobrança dos pacotes. */
    "pagamento",
    /** Contatos e destino comercial da landing pública. */
    "landing",
    /** Credencial e modelo do fal.ai, o segundo provedor de imagem. */
    "fal",
];
export const AI_SERVICES = [
    "image_generate",
    "image_suggest",
    "cost_estimate",
    /** Lote: a visão que descreve a foto e escreve o prompt. */
    "image_analyze",
    /** Lote: a comparação entre o resultado e a foto original. */
    "image_verify",
    /** Impressão: o modelo de imagem que amplia o cenário de uma foto. */
    "impressao_ampliacao",
    /** Marcador de livro: o modelo de imagem que cria a arte de uma faixa. */
    "marcador_arte",
    /** Marcador de livro: a visão que lê o texto da faixa para conferir. */
    "marcador_leitura",
    /** Marcador de livro: o modelo de texto que propõe as três direções de arte. */
    "marcador_direcoes",
    /** Biblioteca de figurinhas: a visão que descreve a imagem recém-enviada (custo da casa, não do cliente). */
    "figurinha_descricao",
];
export const AI_PROVIDERS = ["openrouter", "fal"];
/**
 * Quem gera a imagem. Recorte de `AI_PROVIDERS`: texto continua só no
 * OpenRouter, porque é de lá que saem a sugestão de prompt e a estimativa de
 * custo.
 *
 * A coluna `provider` de `ai_usage_logs` é `text` sem constraint no banco, então
 * acrescentar um provedor aqui não pede migração.
 */
export const IMAGE_PROVIDERS = ["openrouter", "fal"];
/**
 * Os papéis do texto de um marcador de livro, de cima para baixo: título, subtítulo, frase e rodapé.
 *
 * Moram aqui porque dois lados falam deles e precisam ser o mesmo: `zonasDoTexto` (`@foto/shared`) diz
 * onde cada papel fica na arte — e o prompt da arte sem texto reserva essas zonas —, e a sobreposição do
 * servidor (`apps/api/.../sobreposicao-de-texto`) escreve o texto do operador nelas, com o estilo de cada
 * papel. Duas listas, uma em cada lado, divergiriam em silêncio.
 */
export const PAPEIS_DO_TEXTO = ["titulo", "subtitulo", "frase", "rodape"];
/**
 * O que uma imagem da biblioteca de figurinhas é: a figurinha (um desenho recortado), o logo de uma casa
 * ou a arte de letras (um texto desenhado por IA, recortado). A tela filtra por isto, e a descrição
 * automática muda de redação conforme o tipo.
 */
export const TIPOS_DE_FIGURINHA = ["figurinha", "logo", "texto"];
/** De onde a imagem veio: enviada por alguém, ou criada por um modelo de imagem. */
export const ORIGENS_DA_FIGURINHA = ["upload", "ia"];
export const COST_CONFIDENCES = ["exact", "estimated", "unknown"];
/**
 * O ciclo de um trabalho de impressão (a folha enviada à impressora por IPP).
 *
 * `aguardando` inclui o tempo em que a impressora estava ocupada e o servidor tenta de novo; `imprimindo`
 * é daqui até a última cópia sair. Os três últimos são terminais: o worker nunca os reabre, e é por isso
 * que cancelar um trabalho que já terminou é erro, e não um "não faz nada".
 */
export const ESTADOS_DO_TRABALHO_DE_IMPRESSAO = [
    "aguardando",
    "imprimindo",
    "concluido",
    "falhou",
    "cancelado",
];
/**
 * Como o sistema chega à impressora (#109). `direta`: o servidor fala IPP com o endereço cadastrado — serve
 * quando ele roda na rede da impressora. `agente`: o agente de impressão, no notebook do evento, busca as
 * folhas no servidor e imprime na rede local; é o caminho da produção, que está na internet.
 */
export const CONEXOES_DE_IMPRESSORA = ["direta", "agente"];
/** Nomes das filas do pg-boss. Ver apps/api/src/jobs. */
export const QUEUES = {
    imageGenerate: "image-generate",
    imageGenerateDlq: "image-generate-dlq",
    costEstimate: "cost-estimate",
    /**
     * O lote, em três elos.
     *
     * Cada etapa é uma fila própria porque as três têm perfis diferentes:
     * `preparar` roda uma vez por lote, `analisar` e `conferir` são chamadas de
     * segundos que suportam concorrência maior, e a geração no meio delas leva
     * minutos e é limitada pelo provedor. Uma fila só faria a mais lenta ditar o
     * ritmo das outras duas.
     */
    lotePreparar: "lote-preparar",
    loteAnalisar: "lote-analisar",
    loteConferir: "lote-conferir",
    loteDlq: "lote-dlq",
    /**
     * A impressão direta. Uma fila só, de concorrência 1: a SELPHY leva de 40 a 60 s por folha e não tem fila
     * útil, então dois trabalhos ao mesmo tempo só a deixariam ocupada para o segundo.
     */
    impressaoTrabalho: "impressao-trabalho",
    /** Agendada, uma vez por dia: apaga do storage os arquivos de trabalho que já não servem. */
    impressaoLimpeza: "impressao-limpeza",
    /**
     * O envio de uma foto pelo WhatsApp da SER (#105). Concorrência 1 e 3 s entre mensagens: é um número só, de uma
     * API não oficial, e rajada é o que faz o WhatsApp banir o número — com ele param a loja e o atendimento.
     */
    whatsappEnvio: "whatsapp-envio",
};
//# sourceMappingURL=enums.js.map